import * as Sentry from '@sentry/vue';
import { getCountry } from '@/plugins/country/module';
import {
  APP_HANDLED_IGNORE_PATTERNS,
  NOISE_IGNORE_PATTERNS,
  NOISE_DENY_URLS,
  isInjectedNoiseEvent,
  isCssChunkLoadError,
  deferUnlessTearingDown,
  installTeardownTracker
} from '@/utils/sentryNoiseFilters';
import { groupAxiosError } from '@/utils/sentryAxiosGrouping';

const dsn = process.env.VUE_APP_SENTRY_DSN || '';

function isInAppWebView() {
  // ReactNativeWebView is injected by the relocasa-app's WebView shell.
  // Lets us split web vs in-app traffic on a single tag in Sentry.
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

function detectAppPlatform() {
  if (!isInAppWebView()) {
    return 'web';
  }
  const ua = (navigator && navigator.userAgent) || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'app-unknown';
}

// Sentry's built-in crawler filter does not recognise HeadlessChrome (KL-N2).
function isHeadlessBrowser() {
  return (
    typeof navigator !== 'undefined' &&
    /HeadlessChrome/i.test(navigator.userAgent || '')
  );
}

export function initSentry({ Vue, router }) {
  if (!dsn) {
    // Skip silently — local dev with no DSN configured. Logs etc. still
    // go through the existing window.Logger / window.onerror hooks.
    return;
  }

  // PR previews report into the production issues they test against, and
  // bot events are pure quota — neither reaches Sentry at all.
  if (process.env.VUE_APP_SENTRY_ENV === 'pr-test' || isHeadlessBrowser()) {
    return;
  }

  installTeardownTracker();

  const country = (getCountry() || 'se').toLowerCase();
  const surface = isInAppWebView() ? 'app-webview' : 'web';
  const platform = detectAppPlatform();

  Sentry.init({
    app: Vue,
    dsn,
    // process.env.NODE_ENV is 'production' for prod builds, 'development'
    // for `npm run serve`. CI sets VUE_APP_SENTRY_ENV when we want
    // something more granular (e.g. 'release-staging').
    environment: process.env.VUE_APP_SENTRY_ENV || process.env.NODE_ENV,
    // Set by CI from the git SHA; missing in local dev — Sentry will
    // group everything under "(no release)" then.
    release: process.env.VUE_APP_SENTRY_RELEASE || undefined,
    // Performance monitoring — 10% still blew the monthly trace quota in a
    // few days, so sample ~0.33% (30x fewer). Errors are unaffected.
    tracesSampleRate: 0.0033,
    // Source maps coming in a follow-up PR; until then stack traces are
    // minified but errors still flow through.
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.httpClientIntegration()
    ],
    // Only propagate sentry-trace/baggage headers to same-origin
    // (relative) requests and localhost. Our API calls go to separate
    // subdomains (api2.*, proposition2.*, cms.*) which are cross-origin
    // — adding tracing headers there triggers a CORS preflight that the
    // backends don't currently allow `sentry-trace`/`baggage` on, which
    // could break those requests. There's also no benefit yet since the
    // backends aren't instrumented. When backend tracing lands AND the
    // backends are confirmed to allow these headers in CORS, add the
    // cross-origin domains back here.
    tracePropagationTargets: ['localhost', /^\//],
    // Mirror the suppression already done in main.js's
    // window.onunhandledrejection. These are expected/noisy (axios throws
    // "Network Error" on every flaky connection; END_MATCH_CANCELLED is a
    // normal control-flow signal) — the app deliberately ignores them, so
    // keep them out of Sentry too instead of burning quota on noise.
    // All suppression patterns live in sentryNoiseFilters.js with per-issue
    // rationale; denyUrls drops errors thrown by injected scripts by their
    // frame URL.
    ignoreErrors: [...APP_HANDLED_IGNORE_PATTERNS, ...NOISE_IGNORE_PATTERNS],
    denyUrls: NOISE_DENY_URLS,
    beforeSend(event, hint) {
      // Injected-script recursion (no frames from our bundle) — not ours.
      if (isInjectedNoiseEvent(event)) {
        return null;
      }

      // Strip user PII before send — only the id is kept. Matches the RN
      // behaviour so cross-project user lookups still work by id.
      if (event.user) {
        event.user = { id: event.user.id };
      }

      // A failing CSS chunk is almost always the document being torn down
      // mid lazy-load (KL-7) — decide once it has stayed alive for a moment.
      if (isCssChunkLoadError(event)) {
        return deferUnlessTearingDown(event);
      }

      // One issue per failing endpoint and status instead of one for all
      // API calls (KL-3).
      return groupAxiosError(event, hint && hint.originalException);
    },
    initialScope: {
      tags: {
        surface,
        platform,
        country
      }
    }
  });
}

export function setSentryUser(user) {
  if (!dsn) {
    return;
  }
  // fetchUser is the only producer of the app user object and gates on
  // userId. Guard on the user itself: a falsy user must clear, not
  // stringify into a synthetic id that collapses sessions together.
  const userId = user ? user.userId : null;
  if (userId === undefined || userId === null) {
    Sentry.setUser(null);
    Sentry.setTag('impersonated', false);
    return;
  }

  Sentry.setUser({ id: String(userId) });
  // Admin sessions would otherwise inflate the impersonated customer's
  // user counts — tag them so they can be filtered out.
  Sentry.setTag('impersonated', !!user.isImpersonated);
}

export { Sentry };
